import { z } from 'zod';
import { PRODUCT_MEASUREMENT_KINDS, type ProductMeasurementKind } from './catalog';
import { normalizedUuidSchema as uuidSchema } from './common';
import {
  type AreaComponent,
  areaComponentSchema,
  FACE_KEYS,
  type FaceKey,
  faceDefinitionSchema,
  followsMeasurementFrame,
  MAX_COMPONENTS,
  measurementFrameOf,
  type ShapePoint,
  type ShapeVertex,
} from './editor';
import {
  collectDefinitionIssues,
  flattenPolygonRing,
  type SizeMm,
  shapeBoundsOf,
} from './editor-geometry';

// ---------------------------------------------------------------------------
// 편집영역 템플릿 계약 — 카테고리마다 기본 편집영역(면·서피스·인쇄 영역·실측 종류·기본 폰트/잉크/인쇄 방식)을 두고
// 같은 카테고리 상품에 적용하거나, 다른 상품의 편집영역을 복사한다. 설계: docs/design/custom/edit-area-template.md
//
// 템플릿은 상품 편집영역 정의에서 colors(색상별 목업 사진)와 faces.<면>.composite(상품 소유 합성 에셋)를 뺀 기하다.
// 서피스 제어점은 사진 %라서 템플릿 원본과 같은 표준 구도로 찍은 목업에만 맞는다(어드민 안내 문구).
// ---------------------------------------------------------------------------

export const MAX_EDIT_AREA_TEMPLATE_NAME_LENGTH = 50;

/** 템플릿 면 — 상품 면 정의에서 합성 설정(composite)을 뺀 모양. */
export const templateFaceSchema = faceDefinitionSchema.omit({ composite: true });
export type TemplateFace = z.infer<typeof templateFaceSchema>;

/**
 * 템플릿 기하 — components(mm)·faces(surfaces 사진 %·placements mm). 교차 검사는 편집영역 정의와 같은 함수를 쓰되,
 * 색상 사진이 없으므로 색상·합성(마스크 필요 포함) 관련 위반은 보지 않는다 — 그쪽은 상품에 적용한 뒤 저장 검사가 본다.
 */
export const editAreaTemplateGeometrySchema = z
  .strictObject({
    components: z.array(areaComponentSchema).min(1).max(MAX_COMPONENTS),
    faces: z.strictObject({
      front: templateFaceSchema,
      back: templateFaceSchema.optional(),
      left: templateFaceSchema.optional(),
      right: templateFaceSchema.optional(),
    }),
  })
  .superRefine((geometry, ctx) => {
    for (const issue of collectDefinitionIssues({ ...geometry, colors: [] })) {
      if (issue.path[0] === 'faces' && issue.path[2] === 'composite') continue;
      ctx.addIssue({ code: 'custom', path: [...issue.path], message: issue.message });
    }
  });
export type EditAreaTemplateGeometry = z.infer<typeof editAreaTemplateGeometrySchema>;

const measurementSchema = z.strictObject({
  widthMm: z.number().positive(),
  heightMm: z.number().positive(),
});

const templateNameSchema = z
  .string()
  .trim()
  .min(1, '템플릿 이름을 입력해 주세요.')
  .max(
    MAX_EDIT_AREA_TEMPLATE_NAME_LENGTH,
    `템플릿 이름은 ${MAX_EDIT_AREA_TEMPLATE_NAME_LENGTH}자 이하여야 합니다.`,
  );

export const editAreaTemplateSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  /** 기본 템플릿으로 지정한 카테고리. null 이면 직접 적용·복사 전용. */
  categoryId: z.uuid().nullable(),
  /** "대분류 > 소분류" 또는 대분류 이름. categoryId 가 null 이면 null. */
  categoryPath: z.string().nullable(),
  measurementKind: z.enum(PRODUCT_MEASUREMENT_KINDS),
  /** 템플릿을 만든 상품의 실측(원통이면 widthMm 가 지름). */
  measurement: measurementSchema,
  geometry: editAreaTemplateGeometrySchema,
  fontIds: z.array(z.uuid()),
  inkColorIds: z.array(z.uuid()),
  printMethodIds: z.array(z.uuid()),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type EditAreaTemplate = z.infer<typeof editAreaTemplateSchema>;

export const editAreaTemplateListSchema = z.array(editAreaTemplateSchema);

export const createEditAreaTemplateFromProductInputSchema = z.strictObject({
  productId: uuidSchema,
  name: templateNameSchema,
  categoryId: uuidSchema.nullable(),
});
export type CreateEditAreaTemplateFromProductInput = z.infer<
  typeof createEditAreaTemplateFromProductInputSchema
>;

export const updateEditAreaTemplateInputSchema = z.strictObject({
  id: uuidSchema,
  name: templateNameSchema,
  categoryId: uuidSchema.nullable(),
});
export type UpdateEditAreaTemplateInput = z.infer<typeof updateEditAreaTemplateInputSchema>;

export const editAreaTemplateProductInputSchema = z.strictObject({ productId: uuidSchema });

/** 상품에 적용할 카테고리 템플릿 — 대표 소분류의 템플릿, 없으면 그 대분류의 템플릿. 없으면 null. */
export const productEditAreaTemplateSchema = z
  .object({
    template: editAreaTemplateSchema,
    /** 상품 카테고리 체인 중 템플릿을 찾은 카테고리 경로. */
    inheritedFrom: z.string(),
  })
  .nullable();
export type ProductEditAreaTemplate = z.infer<typeof productEditAreaTemplateSchema>;

export const applyEditAreaTemplateInputSchema = z.strictObject({
  productId: uuidSchema,
  templateId: uuidSchema,
});
export type ApplyEditAreaTemplateInput = z.infer<typeof applyEditAreaTemplateInputSchema>;

export const copyEditAreaFromProductInputSchema = z
  .strictObject({ productId: uuidSchema, sourceProductId: uuidSchema })
  .refine((input) => input.productId !== input.sourceProductId, {
    path: ['sourceProductId'],
    message: '같은 상품에서는 복사할 수 없습니다.',
  });
export type CopyEditAreaFromProductInput = z.infer<typeof copyEditAreaFromProductInputSchema>;

/**
 * 적용·복사 결과 — 상품을 준비(실측 종류·실측·기본 폰트/잉크/인쇄 방식)한 뒤 상품 실측에 맞춘 기하.
 * 어드민은 이 기하를 편집 초안에 넣고, 색상별 목업 사진·원통 마스크를 채워 기존 저장으로 새 버전을 만든다.
 */
export const editAreaTemplateApplicationSchema = z.object({
  measurementKind: z.enum(PRODUCT_MEASUREMENT_KINDS),
  measurement: measurementSchema,
  geometry: editAreaTemplateGeometrySchema,
  /** 템플릿 실측과 상품 실측이 달라 인쇄 영역·배치를 비율로 맞췄는가. */
  scaled: z.boolean(),
  /** 이번 적용으로 상품 실측 종류를 바꿨는가(편집영역이 없던 상품만). */
  measurementKindChanged: z.boolean(),
  /** 상품에 하나도 없어서 새로 연결한 기본 항목 수. */
  linked: z.object({
    fonts: z.number().int().min(0),
    inkColors: z.number().int().min(0),
    printMethods: z.number().int().min(0),
  }),
});
export type EditAreaTemplateApplication = z.infer<typeof editAreaTemplateApplicationSchema>;

// ---------------------------------------------------------------------------
// 순수 함수 — api 적용 경로와 어드민 요약이 같이 쓴다.
// ---------------------------------------------------------------------------

const isSame = (a: number, b: number): boolean => Math.abs(a - b) < 1e-9;
/** 비율 축소 뒤 컴포넌트 mm — 0.1mm 아래로 내려 배치 ⊂ 서피스를 지킨다. */
const floorTenth = (value: number): number => Math.max(0.1, Math.floor(value * 10) / 10);

/** 부동소수 찌꺼기만 지우는 반올림(1e-7mm) — 0.1mm 내림은 기울어진 변 위의 구멍 점을 바깥으로 밀어 쓰지 않는다. */
const cleanCoordinate = (value: number): number => Math.round(value * 1e7) / 1e7;

const scalePoint = (point: ShapePoint, sx: number, sy: number): ShapePoint => ({
  xMm: cleanCoordinate(point.xMm * sx),
  yMm: cleanCoordinate(point.yMm * sy),
});

/** 고리 점(바깥·구멍) — 곡선 손잡이도 점과 같은 비율·반올림으로 옮긴다. */
const scaleVertices = (points: readonly ShapeVertex[], sx: number, sy: number): ShapeVertex[] =>
  points.map((point) => ({
    ...scalePoint(point, sx, sy),
    ...(point.handleIn === undefined ? {} : { handleIn: scalePoint(point.handleIn, sx, sy) }),
    ...(point.handleOut === undefined ? {} : { handleOut: scalePoint(point.handleOut, sx, sy) }),
  }));

/**
 * 컴포넌트를 비율로 맞춘다. 다각형은 점·구멍을 내림 없이 같은 비율로 정확히 줄이고(변 위 점·구멍 관계 유지),
 * 외접 사각형(width·height)을 줄인 점에서 다시 구한다 — 점 검증이 외접 사각형 = 컴포넌트 크기를 요구한다.
 * 사각형·타원은 0.1mm 내림(floorTenth) 그대로.
 */
const fitComponent = (component: AreaComponent, sx: number, sy: number): AreaComponent => {
  const shape = component.shape;
  if (shape?.type !== 'polygon') {
    return {
      ...component,
      widthMm: floorTenth(component.widthMm * sx),
      heightMm: floorTenth(component.heightMm * sy),
    };
  }

  const points = scaleVertices(shape.points, sx, sy);
  // 외접 사각형은 풀어 쓴 곡선 고리의 외접이다(손잡이 점 제외).
  const bounds = shapeBoundsOf(flattenPolygonRing(points));

  return {
    ...component,
    widthMm: bounds.widthMm,
    heightMm: bounds.heightMm,
    shape: {
      type: 'polygon',
      points,
      ...(shape.holes === undefined
        ? {}
        : { holes: shape.holes.map((hole) => scaleVertices(hole, sx, sy)) }),
    },
  };
};

/**
 * 템플릿 기하를 대상 상품 실측에 맞춘다. 실측 종류는 같아야 한다(호출자가 맞춘다).
 * - 실측 프레임을 따르는 서피스(flat·cylinder)는 realSize 를 대상 프레임으로 바꾼다. mesh 는 같은 비율로 늘이고 줄인다.
 * - 프레임이 다르면 컴포넌트 크기와 배치 위치를 같은 가로·세로 비율로 맞춘다 — 상대 배치가 그대로 유지된다.
 * - 서피스 제어점(사진 %)은 그대로 둔다 — 표준 구도 목업이면 같은 자리에 맞는다.
 */
export const fitTemplateGeometry = (
  geometry: EditAreaTemplateGeometry,
  measurementKind: ProductMeasurementKind,
  from: SizeMm,
  to: SizeMm,
): { readonly geometry: EditAreaTemplateGeometry; readonly scaled: boolean } => {
  const fromFrame = measurementFrameOf(measurementKind, from);
  const toFrame = measurementFrameOf(measurementKind, to);
  const sx = toFrame.widthMm / fromFrame.widthMm;
  const sy = toFrame.heightMm / fromFrame.heightMm;
  if (isSame(sx, 1) && isSame(sy, 1)) return { geometry, scaled: false };

  const faces: Partial<Record<FaceKey, TemplateFace>> = {};
  for (const key of FACE_KEYS) {
    const face = geometry.faces[key];
    if (face === undefined) continue;
    faces[key] = {
      surfaces: face.surfaces.map((surface) => ({
        ...surface,
        realSize: followsMeasurementFrame(surface.type, measurementKind)
          ? { widthMm: toFrame.widthMm, heightMm: toFrame.heightMm }
          : {
              widthMm: surface.realSize.widthMm * sx,
              heightMm: surface.realSize.heightMm * sy,
            },
      })),
      placements: face.placements.map((placement) => ({
        ...placement,
        position: { xMm: placement.position.xMm * sx, yMm: placement.position.yMm * sy },
      })),
    };
  }

  return {
    scaled: true,
    geometry: {
      components: geometry.components.map((component) => fitComponent(component, sx, sy)),
      faces: {
        front: faces.front ?? geometry.faces.front,
        ...(faces.back === undefined ? {} : { back: faces.back }),
        ...(faces.left === undefined ? {} : { left: faces.left }),
        ...(faces.right === undefined ? {} : { right: faces.right }),
      },
    },
  };
};

/** 편집영역 정의(또는 그 모양)에서 템플릿 기하만 꺼낸다 — 색상 사진·합성 에셋을 버린다. */
export const templateGeometryOf = (definition: {
  readonly components: EditAreaTemplateGeometry['components'];
  readonly faces: Readonly<
    Partial<Record<FaceKey, { readonly surfaces: unknown; readonly placements: unknown }>>
  >;
}): EditAreaTemplateGeometry =>
  editAreaTemplateGeometrySchema.parse({
    components: definition.components,
    faces: Object.fromEntries(
      FACE_KEYS.flatMap((key) => {
        const face = definition.faces[key];
        return face === undefined
          ? []
          : [[key, { surfaces: face.surfaces, placements: face.placements }]];
      }),
    ),
  });
